import * as React from 'react';
import { MouseEvent, useMemo, type JSX } from 'react';
import Image from 'next/image';

import { cn, customImageLoader } from '@restworld/utils-common';

import { Heading } from '@restworld/ui-ds-base';

export const ImageGallery = ({ images, type = 'full', onClick }: ImageGalleryProps) =>
  type === 'full' ? <FullViewGallery images={images} /> : <HalfViewGallery images={images} onClick={onClick} />;
export default ImageGallery;
// --> types
export interface ImageGalleryProps {
  images?: ImageType;
  type: WidthType;
  onClick?: ClickHandlerType;
}
type ClickHandlerType = (e: MouseEvent<HTMLAnchorElement>) => void;
type WidthType = keyof typeof widthMap;
type ImageType = string[];
type ImagesType = {
  images?: ImageType;
  onClick?: ClickHandlerType;
};
// --> components --> full view
const FullViewGallery = ({ images }: ImagesType) => (
  <FullViewWrapper>
    {images?.length ? images.map((v) => <ImageStyled width={400} height={400} key={v} alt="alt" src={v} />) : null}
  </FullViewWrapper>
);
// --> components --> half view
const HalfViewGallery = ({ images, onClick }: ImagesType) => {
  const imagesEl = useMemo(() => {
    const initialImageEl = <ImageElement width="full" imgSrc={images?.[0]} />;
    const remainingImagesEl = images?.map((v, i, list) => {
      if (list?.length - 1 >= 3) {
        const remainImagesLength = list.length - 5;
        if (i >= 0 && i < 3) return <ImageElement key={i} width="half" imgSrc={v} />;
        if (i === 3)
          return (
            <ImageElementWrapper key={i} href="#!" width="half" onClick={onClick}>
              <ImageStyled
                blurDataURL=""
                fill
                sizes="(max-width: 768px) 50vw, 25vw"
                $customStyle={'bg-opacity-50'}
                alt="gallery"
                loading="lazy"
                src={v}
              />
              <CenterElement>
                <Heading as="span" size={6} color="white">{`+${remainImagesLength}`}</Heading>
              </CenterElement>
            </ImageElementWrapper>
          );
      }
      return null;
    });
    return (
      <>
        <ImageElementMainWrapper>{initialImageEl}</ImageElementMainWrapper>
        <ImageElementMainWrapper>{remainingImagesEl}</ImageElementMainWrapper>
      </>
    );
  }, [images, onClick]);
  return (
    <HalfViewMainWrapper>
      <HalfViewSubWrapper>{imagesEl}</HalfViewSubWrapper>
    </HalfViewMainWrapper>
  );
};
// --> child components
const ImageStyled = ({
  $customStyle,
  className,
  ...props
}: { $customStyle?: string } & React.ComponentProps<typeof Image>) => (
  <Image
    loader={customImageLoader}
    className={cn('block object-cover object-center w-full h-full rounded-lg', $customStyle, className)}
    {...props}
  />
);
const FullViewWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2 h-full py-2', className)} {...props} />
);
const HalfViewMainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('xs:px-0', className)} {...props} />
);
const HalfViewSubWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-wrap h-full', className)} {...props} />
);
const CenterElement = ({ children }: { children: JSX.Element }) => (
  <div className="absolute top-1/2 left-1/2 -mr-[50%] -translate-x-2/4 -translate-y-2/4 z-50">{children}</div>
);
const ImageElement = ({ imgSrc, width }: { imgSrc?: string; width: WidthType }) => (
  <ImageElementWrapper width={width}>
    {imgSrc ? <ImageStyled height={400} width={400} alt="" src={imgSrc} /> : null}
  </ImageElementWrapper>
);
const ImageElementMainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-wrap w-1/2', className)} {...props} />
);
const ImageElementWrapper = ({ width, className, ...props }: any) => (
  <a className={cn('p-1 relative', widthMap[width], className)} {...props} />
);
// --> constants
const widthMap = {
  full: 'w-full',
  half: 'w-1/2',
};
